import { useState, useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const colors = {
  coral: '#E8847C',
  coralLight: '#F5AEA8',
  mint: '#7ECEC1',
  mintLight: '#b8e4dd',
  cream: '#FFF9F5',
  warmGray: '#4A4543',
};

function useScrollReveal() {
  const [visible, setVisible] = useState<Record<string, boolean>>({ hero: true });
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const targets = container.querySelectorAll('[data-reveal]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = (entry.target as HTMLElement).dataset.reveal;
            if (id) setVisible((v) => ({ ...v, [id]: true }));
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -60px 0px' }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return { visible, containerRef };
}

const SHOW_SARAYA = false; /* 一時非表示 */
const SHOW_ROHTO = true;

const SPONSOR_IMAGES_SARAYA = [
  { src: '/images/saraya-lactoferrin-1.jpg?v=1', alt: 'ラクトフェリンLab. サラヤ株式会社様 協賛品1' },
  { src: '/images/saraya-lactoferrin-2.jpg?v=1', alt: 'ラクトフェリンLab. サラヤ株式会社様 協賛品2' },
];

const SPONSOR_IMAGES_ZUKA = [
  { src: '/images/zuka-maison-de-ibee.png?v=1', alt: 'JARRAH HONEY GOLD 340g - Maison de i-bee 株式会社ZUKA様 協賛品' },
];

const SPONSOR_IMAGES_HERLIFELAB = [
  { src: '/images/herlifelab-menopause-support.png?v=4', alt: 'HerLifeLab株式会社 更年期サポート' },
];

const SPONSOR_IMAGES_ROHTO = [
  { src: '/images/rohto-profa-cell-alive.png', alt: 'ロート製薬株式会社様 PROFA Cell Alive（10名様）' },
  { src: '/images/rohto-mocola.png', alt: 'ロート製薬株式会社様 MOCOLA ダブルインパクトショット（10名様）' },
];

const SPONSOR_IMAGES_KIMONO_BEAUTY = [
  { src: '/images/kimono-beauty-rental-ticket.png', alt: '一般社団法人きものビューティージャパン協会 きものレンタル無料チケット（10名様）' },
];

const SPONSOR_IMAGES_GINZA_HAGOROMO = [
  { src: '/images/ginza-hagoromo-dinner-ticket.png', alt: '銀座 羽衣 中華ディナーペアチケット（2名様）' },
];

const SPONSOR_IMAGES_WIMMINS = [
  { src: '/images/wimmins-millapis-ex.png?v=3', alt: 'ミラピスEX - 医療法人社団ウィミンズ・ウェルネス' },
  { src: '/images/wimmins-omamorich.png?v=3', alt: 'omamorich インナーショットジェル - 医療法人社団ウィミンズ・ウェルネス' },
];

const SPONSOR_IMAGES_HOLLYWOOD = [
  { src: '/images/hollywood-products-1.png?v=4', alt: 'ハリウッド株式会社 協賛品（ソイプロビューティ・オリジナルタンブラー・長寿の食卓）' },
  { src: '/images/hollywood-products-2.png?v=4', alt: 'ハリウッド株式会社 協賛品（ザ・ハリウッドクリーム・長寿の食卓）' },
];

const SPONSOR_IMAGES_MONEY_HEALTH = [
  { src: '/images/money-health-support.png?v=4', alt: 'マネーヘルスサポート 無料個別相談（90分）国際女性デー記念 Special Gift Ticket' },
];

const SPONSOR_IMAGES_DAMMANN = [
  { src: '/images/dammann-freres-tea.png?v=3', alt: 'DAMMANN FRÈRES 紅茶ギフトセット' },
];

const SPONSOR_IMAGES_GIRL_POWER = [
  { src: '/images/girl-power-igs4000-serum.png?v=1', alt: 'iGS4000ダブルセラム 150ml - 一般社団法人ガールパワー 協賛品' },
];

const SPONSOR_IMAGES_SARAYA_FULL = [
  { src: '/images/saraya-lactoferrin-skincare.png', alt: 'サラヤ株式会社 Lactoferrin Lab. スキンケアセット（3名様）' },
];

const SPONSOR_IMAGES_RECELLLAB = [
  { src: '/images/recelllab-femderm-fc-wash.png?v=1', alt: 'femderm FCウォッシュ 145ml - 株式会社リセルラボ様 協賛品（30名様）' },
];

const SPONSOR_IMAGES_ADVANCED_MEDICAL = [
  { src: '/images/est-re-femcare-special-set.png?v=1', alt: 'est\'re フェムケア Special set - 株式会社アドバンスト・メディカル・ケア (5名様)' },
];

const SPONSOR_IMAGES_NANADECOR = [
  { src: '/images/nanadecor-pillow-mist-towel-set.png', alt: 'nanadecor by 有限会社Juliette ジャルダンナナデェコール ピローミストとミニタオルのセット（3名様）' },
  { src: '/images/nanadecor-nightdress.png', alt: 'nanadecor by 有限会社Juliette オーガニックコットンのナイトドレス（1名様）' },
];

const SPONSOR_IMAGES_SANRIO_PUROLAND = [
  { src: '/images/sanrio-puroland-goods.png', alt: '株式会社サンリオピューロランド ピューロランドグッズ詰め合わせ（5名様）' },
  { src: '/images/sanrio-puroland-passport.png', alt: '株式会社サンリオピューロランド ピューロランドパスポート 2枚×5組（5名様）' },
];

const SPONSOR_IMAGES_TSUKIJI_BON_MARCHE = [
  { src: '/images/tsukiji-bon-marche-italian-dinner.png', alt: '築地 共栄会 ボン・マルシェ Italian Pair Dinner Ticket（3名様）' },
];

const SPONSOR_IMAGES_GINZA_WAGYU = [
  { src: '/images/ginza-wagyu-yakiniku.png', alt: '銀座WAGYU 焼肉お食事券（2名様）' },
];

const SPONSOR_IMAGES_JBP = [
  { src: '/images/jbp-placenta-skincare.png', alt: '株式会社日本生物製剤 プラセンタスキンケア 化粧品セット（5名様）' },
];

const SPONSOR_IMAGES = [
  ...(SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA : []),
  ...SPONSOR_IMAGES_ZUKA,
  ...SPONSOR_IMAGES_HERLIFELAB,
  ...(SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO : []),
  ...SPONSOR_IMAGES_KIMONO_BEAUTY,
  ...SPONSOR_IMAGES_GINZA_HAGOROMO,
  ...SPONSOR_IMAGES_WIMMINS,
  ...SPONSOR_IMAGES_HOLLYWOOD,
  ...SPONSOR_IMAGES_MONEY_HEALTH,
  ...SPONSOR_IMAGES_DAMMANN,
  ...SPONSOR_IMAGES_GIRL_POWER,
  ...SPONSOR_IMAGES_SARAYA_FULL,
  ...SPONSOR_IMAGES_RECELLLAB,
  ...SPONSOR_IMAGES_ADVANCED_MEDICAL,
  ...SPONSOR_IMAGES_NANADECOR,
  ...SPONSOR_IMAGES_SANRIO_PUROLAND,
  ...SPONSOR_IMAGES_TSUKIJI_BON_MARCHE,
  ...SPONSOR_IMAGES_GINZA_WAGYU,
  ...SPONSOR_IMAGES_JBP,
];

export default function EventSignificance() {
  const { visible, containerRef } = useScrollReveal();
  const [sponsorLightboxOpen, setSponsorLightboxOpen] = useState(false);
  const [sponsorLightboxIndex, setSponsorLightboxIndex] = useState(0);

  useEffect(() => {
    if (!sponsorLightboxOpen) return;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSponsorLightboxOpen(false);
      if (e.key === 'ArrowLeft') setSponsorLightboxIndex((i) => (i === 0 ? SPONSOR_IMAGES.length - 1 : i - 1));
      if (e.key === 'ArrowRight') setSponsorLightboxIndex((i) => (i === SPONSOR_IMAGES.length - 1 ? 0 : i + 1));
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [sponsorLightboxOpen]);

  const reveal = (id: string) =>
    `transition-all duration-[1200ms] ease-out ${
      visible[id] ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-16 scale-[0.97]'
    }`;

  return (
    <div
      className="min-h-screen bg-white overflow-x-hidden"
      style={{ fontFamily: "'Zen Maru Gothic', sans-serif" }}
    >
      <style>{`
        @keyframes orbFloat {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
          33% { transform: translate(30px, -20px) scale(1.08); opacity: 0.55; }
          66% { transform: translate(-25px, -30px) scale(0.92); opacity: 0.48; }
        }
        @keyframes orbFloatSlow {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-30px, 20px) scale(1.05); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.3; filter: blur(90px); }
          50% { opacity: 0.5; filter: blur(110px); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <Header variant="event" />

      <main className="pt-24" ref={containerRef}>
        {/* Hero – アイキャッチ＋ページタイトル */}
        <section className="relative py-24 overflow-hidden" style={{ backgroundColor: colors.cream }}>
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute -top-32 -right-32 w-[550px] h-[550px] rounded-full"
              style={{ background: `radial-gradient(circle, ${colors.coralLight} 0%, transparent 70%)`, filter: 'blur(90px)', animation: 'orbFloat 16s ease-in-out infinite' }}
            />
            <div
              className="absolute top-1/3 -left-24 w-[450px] h-[450px] rounded-full"
              style={{ background: `radial-gradient(circle, ${colors.mintLight} 0%, transparent 70%)`, filter: 'blur(100px)', animation: 'orbFloatSlow 20s ease-in-out infinite 2s' }}
            />
          </div>

          <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 transition-all duration-[1200ms] ease-out ${visible['hero'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-reveal="hero">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* アイキャッチ写真（前回イベントの集合写真） */}
              <div className="relative order-2 lg:order-1">
                <div
                  className="absolute -top-6 -left-6 w-full h-full rounded-3xl"
                  style={{ backgroundColor: `${colors.mintLight}50` }}
                />
                <div className="relative">
                  <div className="w-full aspect-[4/3] sm:aspect-[3/2] min-h-[280px] rounded-3xl overflow-hidden shadow-2xl">
                    <img
                      src="/images/event.jpg"
                      alt="昨年度の国際女性デーイベントの様子"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <p className="mt-3 text-center text-gray-500 text-sm">
                    昨年度（２０２５年３月８日開催）のイベントの様子
                  </p>
                </div>
              </div>

              {/* ページタイトル・見出し */}
              <div className="order-1 lg:order-2">
                <span
                  className="inline-block px-5 py-2 text-xs font-bold tracking-[0.2em] uppercase mb-8"
                  style={{
                    backgroundColor: `${colors.coralLight}40`,
                    color: colors.coral,
                    borderRadius: '100px',
                  }}
                >
                  Event Value
                </span>
                <h1
                  className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight mb-8"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  イベントの<br />
                  <span style={{ color: colors.coral, textShadow: `0 4px 20px ${colors.coral}40` }}>価値</span>
                </h1>
                <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed mb-6">
                  意味のある1日を、<br className="hidden sm:block" />
                  <span className="font-bold" style={{ color: colors.warmGray }}>一緒に過ごしませんか</span>
                </p>
                <p className="text-base sm:text-lg text-gray-500 leading-relaxed">
                  なんとなくの日常の中で、「意味のある1日を生きたい」と思ったこと、ありませんか。<br />
                  自分のために過ごした時間が、いつの間にか誰かの力にもなっている。そんな時間を一緒に過ごしませんか。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 1 – ふと立ち止まる瞬間 */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-white"></div>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[650px] h-[650px] rounded-full opacity-30" style={{ background: `radial-gradient(circle, ${colors.mintLight} 0%, transparent 60%)`, filter: 'blur(110px)', animation: 'pulseGlow 8s ease-in-out infinite' }} />
          </div>

          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div data-reveal="s1" className={reveal('s1')}>
              <h2 className="text-2xl sm:text-3xl font-black mb-12 tracking-tight" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                ふと立ち止まる瞬間
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-10">
                毎日がんばってると、「このままでいいのかな」って、ふと立ち止まる瞬間、ありますよね。
                <br />
                忙しさの中で、自分のことは後回しになりがち。
              </p>
              {/* UD対応: 白背景＋濃いグレー文字で十分なコントラスト */}
              <div
                className="py-14 px-10 bg-white rounded-2xl transition-all duration-[1200ms] ease-out"
                style={{
                  boxShadow: visible['s1'] ? `0 30px 90px rgba(74,69,67,0.15), 0 0 0 1px rgba(126,206,193,0.2)` : `0 20px 60px rgba(74,69,67,0.08)`,
                }}
              >
                <p className="text-2xl sm:text-3xl font-black leading-relaxed mb-6" style={{ color: colors.warmGray }}>
                  この日は、がんばらなくていい日。<br />
                  正解を出さなくていい日。
                </p>
                <p className="text-xl font-bold" style={{ color: colors.warmGray }}>
                  でも、自分の気持ちにだけは、ちゃんと耳を傾ける日です。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 – 楽しい時間 */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0" style={{ backgroundColor: colors.cream }}></div>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute bottom-0 left-0 w-[550px] h-[550px] rounded-full opacity-35" style={{ background: `radial-gradient(circle, ${colors.coralLight} 0%, transparent 60%)`, filter: 'blur(100px)', animation: 'orbFloat 12s ease-in-out infinite' }} />
            <div className="absolute top-1/2 right-0 w-[400px] h-[400px] rounded-full opacity-30" style={{ background: `radial-gradient(circle, ${colors.mintLight} 0%, transparent 60%)`, filter: 'blur(90px)', animation: 'orbFloatSlow 15s ease-in-out infinite 1s' }} />
          </div>

          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div data-reveal="s2" className={reveal('s2')}>
              <h2 className="text-2xl sm:text-3xl font-black mb-12 tracking-tight" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                話を聞いて、作品に触れて
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                話を聞いて、作品に触れて、きれいなものを見て、人と笑って。
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                楽しい時間の中で、ふと誰かの言葉に心が揺れたり、
                <br />
                「もしかしたら、自分にもまだ可能性があるかも」と思えたり。
                <br />
                <span className="font-bold" style={{ color: colors.warmGray }}>そんな瞬間が、きっとあります。</span>
              </p>
            </div>
          </div>
        </section>

        {/* Section 3 – イベント内容 */}
        <section className="relative py-24 overflow-hidden bg-white">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-1/4 w-[450px] h-[450px] rounded-full opacity-25" style={{ background: `radial-gradient(circle, ${colors.mint} 0%, transparent 60%)`, filter: 'blur(110px)', animation: 'pulseGlow 10s ease-in-out infinite' }} />
          </div>

          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div data-reveal="s3" className={reveal('s3')}>
              <h2 className="text-2xl sm:text-3xl font-black mb-12 tracking-tight" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                トーク、アート、着物ショー、交流タイム
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                ひとり参加も大歓迎。知らない人同士なのに、なぜか居心地がいい。
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                このイベントに共感した企業や作家さんからのちょっと嬉しい贈り物や体験も用意しています。
                <br />
                当日は、協賛企業からのプレゼントが当たる抽選会もあります。
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 – 価値のある1日（ドラマティックなダークトーン） */}
        <section className="relative py-24 overflow-hidden" style={{ backgroundColor: colors.warmGray }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 w-[800px] h-[800px] rounded-full opacity-30" style={{ background: `radial-gradient(circle, ${colors.coral} 0%, transparent 50%)`, filter: 'blur(120px)' }} />
            <div className="absolute bottom-0 right-1/3 w-[600px] h-[600px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.mint} 0%, transparent 50%)`, filter: 'blur(100px)' }} />
          </div>

          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div data-reveal="s4" className={reveal('s4')}>
              <p className="text-2xl sm:text-4xl font-black leading-relaxed mb-10 text-white" style={{ fontFamily: "'Shippori Mincho', serif" }}>
                楽しかった記憶と、少しだけ前向きになった気持ちが残る。<br />
                それだけで、十分価値のある1日です。
              </p>
              <p className="text-xl text-white/90">
                なんとなく惹かれたなら、それが参加のサインかもしれません。<br />
                新しい出会いと、未来が少し動き出す1日に。
              </p>
            </div>
          </div>
        </section>

        {/* CTA – ドラマティック */}
        <section className="relative py-24 overflow-hidden" style={{ backgroundColor: colors.coral }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-20 w-96 h-96 rounded-full opacity-25 bg-white" style={{ filter: 'blur(100px)', animation: 'orbFloat 12s ease-in-out infinite' }} />
            <div className="absolute bottom-20 right-20 w-[500px] h-[500px] rounded-full opacity-20 bg-white" style={{ filter: 'blur(120px)', animation: 'orbFloatSlow 15s ease-in-out infinite' }} />
          </div>

          <div className="max-w-4xl mx-auto px-4 text-center relative">
            <div data-reveal="cta" className={reveal('cta')}>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-10 leading-tight" style={{ fontFamily: "'Shippori Mincho', serif" }}>
                ぜひ会場でお会いしましょう
              </h2>
              <a
                href="https://peatix.com/event/4805808/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-4 px-12 py-5 text-xl font-bold text-white rounded-xl bg-white/25 hover:bg-white/35 backdrop-blur-md transition-all duration-300 border-2 border-white hover:scale-105"
              >
                <i className="ri-ticket-line text-3xl"></i>
                お申込みはこちら
              </a>
              <p className="mt-8 text-white/80 text-sm">※ Peatixの外部サイトに移動します</p>
            </div>
          </div>
        </section>

        {/* 全体見出し：当日のプログラムへ誘う */}
        <section className="relative py-20 overflow-hidden" style={{ backgroundColor: colors.warmGray }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.coral} 0%, transparent 50%)`, filter: 'blur(80px)' }} />
          </div>
          <div className={`max-w-4xl mx-auto px-4 text-center relative ${reveal('program-head')}`} data-reveal="program-head">
            <p className="text-white/70 text-sm font-bold tracking-[0.3em] uppercase mb-4">Program</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight" style={{ fontFamily: "'Shippori Mincho', serif" }}>
              幕が開く。<br className="sm:hidden" />
              <span className="inline-block mt-2">その先に、あなたを待つもの。</span>
            </h2>
          </div>
        </section>

        {/* オープニングアクト コンテンポラリー */}
        <section className="relative py-20 overflow-hidden" style={{ backgroundColor: colors.cream }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.mintLight} 0%, transparent 60%)`, filter: 'blur(100px)' }} />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.coralLight} 0%, transparent 60%)`, filter: 'blur(100px)' }} />
          </div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className={`${reveal('opening-act')}`} data-reveal="opening-act">
              <div className="text-center mb-12">
                <span
                  className="inline-block px-5 py-2 text-xs font-bold tracking-[0.2em] uppercase mb-4"
                  style={{
                    backgroundColor: `${colors.mintLight}60`,
                    color: colors.mint,
                    borderRadius: '100px',
                  }}
                >
                  Opening Act
                </span>
                <h2
                  className="text-2xl sm:text-3xl font-black tracking-tight mb-2"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  オープニングアクト　コンテンポラリー
                </h2>
                <p className="text-lg font-bold" style={{ color: colors.coral, fontFamily: "'Shippori Mincho', serif" }}>
                  響喜炎舞「源氏物語より」
                </p>
              </div>

              {/* アイキャッチ画像 */}
              <div className="rounded-2xl overflow-hidden shadow-2xl mb-12 aspect-[16/9]">
                <img
                  src="/images/opening-act-eyecatch.png"
                  alt="響喜炎舞「源氏物語より」ステージ"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* あらすじ */}
              <div className="max-w-3xl mx-auto mb-14">
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed text-center">
                  時は平安時代。紫式部の作品「源氏物語」のアナザーストーリー。
                  光源氏が最期を迎える…その瞬間にスポットを当てた、熱く燃ゆる光源氏の情熱と、愛憎渦巻く女たちの世界。
                  和装姿で繰り広げられる響喜炎舞。皆様を時代絵巻の世界へと誘います。
                </p>
              </div>

              {/* 出演者 */}
              <h3 className="text-lg font-black tracking-tight mb-8" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                出演者
              </h3>

              {/* 寺倉礼那 - 主宰・メイン：大きくインパクトあるレイアウト */}
              <div className="mb-14">
                <div
                  className="flex flex-col lg:flex-row gap-6 lg:gap-10 p-6 sm:p-8 rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: 'white',
                    boxShadow: '0 8px 32px rgba(74,69,67,0.12), 0 0 0 1px rgba(126,206,193,0.2)',
                  }}
                >
                  <div className="flex-shrink-0 w-full lg:w-[320px] aspect-[3/4] max-h-[420px] rounded-xl overflow-hidden shadow-lg">
                    <img src="/images/opening-act-terakura.png" alt="寺倉礼那" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 flex flex-col justify-center min-w-0">
                    <p className="text-xs font-bold tracking-wider mb-2" style={{ color: colors.mint }}>FOR DANCERʼS LIFE 主宰</p>
                    <h4 className="text-2xl sm:text-3xl font-black mb-4" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>
                      寺倉 礼那
                    </h4>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      3歳から松岡怜子バレエ団にてバレエを始める。2016 ミス・ユニバースジャパン広島 ファイナリスト。New York・Melbourne に留学。大阪芸術大学大学院 芸術研究科博士課程前期芸術制作専攻（舞台）卒業。Grand fete competition de ballet 2022 コンテンポラリー部門 1位。Japan Ballet Competition 2023 名古屋新春 コンテンポラリー部門 第2位・芸術賞。ノースカロライナ州立芸術大学 Professional Dance Study 2023 参加。
                    </p>
                  </div>
                </div>
              </div>

              {/* 5名：3列＋2列のグリッド、写真を大きく */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <div className="group">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-lg mb-3 ring-2 ring-transparent group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-coral/30 transition-all duration-300">
                    <img src="/images/opening-act-nishi.png" alt="西櫻子" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-black text-base sm:text-lg" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>西櫻子</p>
                  <p className="text-xs text-gray-500">にし さくらこ</p>
                </div>
                <div className="group">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-lg mb-3 ring-2 ring-transparent group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-coral/30 transition-all duration-300">
                    <img src="/images/opening-act-tsukifuji.png" alt="築藤佳奈" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-black text-base sm:text-lg" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>築藤 佳奈</p>
                  <p className="text-xs text-gray-500">つきふじ かな</p>
                </div>
                <div className="group col-span-2 lg:col-span-1">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-lg mb-3 ring-2 ring-transparent group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-coral/30 transition-all duration-300">
                    <img src="/images/opening-act-mizuguchi.png" alt="水口真穂" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-black text-base sm:text-lg" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>水口 真穂</p>
                  <p className="text-xs text-gray-500">みずぐち まほ</p>
                </div>
                <div className="group">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-lg mb-3 ring-2 ring-transparent group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-coral/30 transition-all duration-300">
                    <img src="/images/opening-act-shiraishi.png" alt="白石雛子" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-black text-base sm:text-lg" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>白石 雛子</p>
                  <p className="text-xs text-gray-500">しらいし ひなこ</p>
                </div>
                <div className="group">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-lg mb-3 ring-2 ring-transparent group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-offset-2 group-hover:ring-offset-white group-hover:ring-coral/30 transition-all duration-300">
                    <img src="/images/opening-act-kitanishi.png" alt="北西菜々香" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-black text-base sm:text-lg" style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}>北西 菜々香</p>
                  <p className="text-xs text-gray-500">きたにし ななか</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 協賛品のご紹介 */}
        <section className="relative py-24 overflow-hidden bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div data-reveal="sponsor" className={reveal('sponsor')}>
              <div className="text-center mb-16">
                <span
                  className="inline-block px-5 py-2 text-xs font-bold tracking-[0.2em] uppercase mb-6"
                  style={{
                    backgroundColor: `${colors.mintLight}60`,
                    color: colors.mint,
                    borderRadius: '100px',
                  }}
                >
                  Sponsored Products
                </span>
                <h2
                  className="text-3xl sm:text-4xl font-black tracking-tight mb-8"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  女性のWell-Being応援 チャリティ抽選会 協賛品のご案内
                </h2>
              </div>

              {SHOW_SARAYA && (
                <div className={`mb-16 ${reveal('sponsor-saraya')}`} data-reveal="sponsor-saraya">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    サラヤ株式会社様
                  </h2>
                  <div className="grid grid-cols-2 gap-4 sm:gap-8">
                    {SPONSOR_IMAGES_SARAYA.map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => { setSponsorLightboxIndex(i); setSponsorLightboxOpen(true); }}
                        className="relative group text-left cursor-pointer"
                      >
                        <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                          <img
                            src={img.src}
                            alt={img.alt}
                            className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <span className="sr-only">拡大表示</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ZUKA と HerLifeLab を横並び */}
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 mb-16 ${reveal('sponsor-zuka')}`} data-reveal="sponsor-zuka">
                {/* Maison de i-bee 株式会社ZUKA様 */}
                <div className="w-full">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    株式会社ZUKA様
                  </h2>
                  <div className="w-full">
                    {SPONSOR_IMAGES_ZUKA.map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + i); setSponsorLightboxOpen(true); }}
                        className="block w-full text-left relative group cursor-pointer"
                      >
                        <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50 w-full">
                          <img
                            src={img.src}
                            alt={img.alt}
                            className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <span className="sr-only">拡大表示</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* HerLifeLab株式会社様 */}
                <div className={`w-full ${reveal('sponsor-herlifelab')}`} data-reveal="sponsor-herlifelab">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    HerLifeLab株式会社様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_HERLIFELAB[0].src}
                        alt={SPONSOR_IMAGES_HERLIFELAB[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>
              </div>

              {SHOW_ROHTO && (
              <div className={`mb-16 ${reveal('sponsor-rohto')}`} data-reveal="sponsor-rohto">
                <h2
                  className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  ロート製薬株式会社様
                </h2>
                <div
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 p-6 sm:p-8 rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: `${colors.cream}`,
                    boxShadow: '0 4px 24px rgba(74,69,67,0.08), 0 0 0 1px rgba(126,206,193,0.15)',
                  }}
                >
                  {/* 左カラム：PROFA Cell Alive */}
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length); setSponsorLightboxOpen(true); }}
                      className="block w-full text-left cursor-pointer group flex-1"
                    >
                      <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                        <img
                          src={SPONSOR_IMAGES_ROHTO[0].src}
                          alt={SPONSOR_IMAGES_ROHTO[0].alt}
                          className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <span className="sr-only">拡大表示</span>
                    </button>
                  </div>
                  {/* 右カラム：MOCOLA */}
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + 1); setSponsorLightboxOpen(true); }}
                      className="block w-full text-left cursor-pointer group flex-1"
                    >
                      <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                        <img
                          src={SPONSOR_IMAGES_ROHTO[1].src}
                          alt={SPONSOR_IMAGES_ROHTO[1].alt}
                          className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <span className="sr-only">拡大表示</span>
                    </button>
                  </div>
                </div>
              </div>
              )}

              {/* きものビューティージャパン協会 と 銀座 羽衣 を横並び */}
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 mb-16 ${reveal('sponsor-kimono')}`} data-reveal="sponsor-kimono">
                <div className="w-full">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    一般社団法人きものビューティージャパン協会様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0)); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_KIMONO_BEAUTY[0].src}
                        alt={SPONSOR_IMAGES_KIMONO_BEAUTY[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>
                <div className={`w-full ${reveal('sponsor-hagoromo')}`} data-reveal="sponsor-hagoromo">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    味の中華 羽衣 銀座本店様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_GINZA_HAGOROMO[0].src}
                        alt={SPONSOR_IMAGES_GINZA_HAGOROMO[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>
              </div>

              {/* 医療法人社団ウィミンズ・ウェルネス */}
              <div className={`mb-16 ${reveal('sponsor-wimmins')}`} data-reveal="sponsor-wimmins">
                <h2
                  className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  医療法人社団ウィミンズ・ウェルネス様
                </h2>
                <div
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 p-6 sm:p-8 rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: `${colors.cream}`,
                    boxShadow: '0 4px 24px rgba(74,69,67,0.08), 0 0 0 1px rgba(126,206,193,0.15)',
                  }}
                >
                  {/* 左カラム：ミラピスEX */}
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length); setSponsorLightboxOpen(true); }}
                      className="block w-full text-left cursor-pointer group flex-1"
                    >
                      <div className="rounded-xl overflow-hidden shadow-md transition-all duration-300 group-hover:shadow-lg group-hover:-translate-y-0.5 bg-white flex items-center justify-center p-6 aspect-[4/3]">
                        <img
                          src={SPONSOR_IMAGES_WIMMINS[0].src}
                          alt={SPONSOR_IMAGES_WIMMINS[0].alt}
                          className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                        />
                      </div>
                      <span className="sr-only">拡大表示</span>
                    </button>
                    <a
                      href="https://w-wellness.jp/millapis/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 py-2 px-3 -mx-3 mt-4 rounded-lg hover:bg-white/60 transition-colors group/link"
                      style={{ color: colors.warmGray }}
                    >
                      <i className="ri-external-link-line text-sm opacity-60 group-hover/link:opacity-100" style={{ color: colors.coral }}></i>
                      <span className="group-hover/link:underline">ミラピスEX　5個</span>
                    </a>
                  </div>
                  {/* 右カラム：omamorich */}
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + 1); setSponsorLightboxOpen(true); }}
                      className="block w-full text-left cursor-pointer group flex-1"
                    >
                      <div className="rounded-xl overflow-hidden shadow-md transition-all duration-300 group-hover:shadow-lg group-hover:-translate-y-0.5 bg-white flex items-center justify-center p-6 aspect-[4/3]">
                        <img
                          src={SPONSOR_IMAGES_WIMMINS[1].src}
                          alt={SPONSOR_IMAGES_WIMMINS[1].alt}
                          className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                        />
                      </div>
                      <span className="sr-only">拡大表示</span>
                    </button>
                    <a
                      href="https://omamorich.jp/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 py-2 px-3 -mx-3 mt-4 rounded-lg hover:bg-white/60 transition-colors group/link"
                      style={{ color: colors.warmGray }}
                    >
                      <i className="ri-external-link-line text-sm opacity-60 group-hover/link:opacity-100" style={{ color: colors.coral }}></i>
                      <span className="group-hover/link:underline">omamorichインナーショットジェル1本入り　10個</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* ハリウッド株式会社 */}
              <div className={`mb-16 ${reveal('sponsor-hollywood')}`} data-reveal="sponsor-hollywood">
                <h2
                  className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  ハリウッド株式会社様
                </h2>
                <div
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 p-6 sm:p-8 rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: `${colors.cream}`,
                    boxShadow: '0 4px 24px rgba(74,69,67,0.08), 0 0 0 1px rgba(126,206,193,0.15)',
                  }}
                >
                  {/* 左カラム：ソイプロ・タンブラー・長寿の食卓 */}
                  <div className="flex flex-col items-start">
                    <button
                      type="button"
                      onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length); setSponsorLightboxOpen(true); }}
                      className="block w-full text-left cursor-pointer group"
                    >
                      <div className="rounded-xl overflow-hidden shadow-md transition-all duration-300 group-hover:shadow-lg group-hover:-translate-y-0.5 bg-white flex items-center justify-center p-6 aspect-[4/3]">
                        <img
                          src={SPONSOR_IMAGES_HOLLYWOOD[0].src}
                          alt={SPONSOR_IMAGES_HOLLYWOOD[0].alt}
                          className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                        />
                      </div>
                      <span className="sr-only">拡大表示</span>
                    </button>
                    <ul className="mt-4 space-y-2">
                      <li>
                        <a
                          href="https://hollywood-jp.com/product/soyproteinbeauty/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 py-2 px-3 -mx-3 rounded-lg hover:bg-white/60 transition-colors group/link"
                          style={{ color: colors.warmGray }}
                        >
                          <i className="ri-external-link-line text-sm opacity-60 group-hover/link:opacity-100" style={{ color: colors.coral }}></i>
                          <span className="group-hover/link:underline">ソイプロビューティ（200g）× 3種</span>
                        </a>
                      </li>
                      <li className="text-gray-600 py-2 px-3 -mx-3">オリジナルタンブラー</li>
                      <li className="text-gray-600 py-2 px-3 -mx-3">メイ牛山 著「長寿の食卓」</li>
                    </ul>
                  </div>
                  {/* 右カラム：ザ・ハリウッドクリーム・長寿の食卓 */}
                  <div className="flex flex-col items-start">
                    <button
                      type="button"
                      onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + 1); setSponsorLightboxOpen(true); }}
                      className="block w-full text-left cursor-pointer group"
                    >
                      <div className="rounded-xl overflow-hidden shadow-md transition-all duration-300 group-hover:shadow-lg group-hover:-translate-y-0.5 bg-white flex items-center justify-center p-6 aspect-[4/3]">
                        <img
                          src={SPONSOR_IMAGES_HOLLYWOOD[1].src}
                          alt={SPONSOR_IMAGES_HOLLYWOOD[1].alt}
                          className="max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-[1.02] transition-transform duration-300"
                        />
                      </div>
                      <span className="sr-only">拡大表示</span>
                    </button>
                    <ul className="mt-4 space-y-2">
                      <li>
                        <a
                          href="https://hollywood-jp.com/product/the-hollywood/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 py-2 px-3 -mx-3 rounded-lg hover:bg-white/60 transition-colors group/link"
                          style={{ color: colors.warmGray }}
                        >
                          <i className="ri-external-link-line text-sm opacity-60 group-hover/link:opacity-100" style={{ color: colors.coral }}></i>
                          <span className="group-hover/link:underline">ザ・ハリウッドクリーム（30g）</span>
                        </a>
                      </li>
                      <li className="text-gray-600 py-2 px-3 -mx-3">メイ牛山 著「長寿の食卓」</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* マネーヘルスサポート と 株式会社フランスティーコーポレーション を横並び */}
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 mb-16 ${reveal('sponsor-money-health')}`} data-reveal="sponsor-money-health">
                {/* マネーヘルスサポート様 */}
                <div className="w-full">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    マネーヘルスサポート様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_MONEY_HEALTH[0].src}
                        alt={SPONSOR_IMAGES_MONEY_HEALTH[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>

                {/* 株式会社フランスティーコーポレーション様 */}
                <div className={`w-full ${reveal('sponsor-dammann')}`} data-reveal="sponsor-dammann">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    株式会社フランスティーコーポレーション様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_DAMMANN[0].src}
                        alt={SPONSOR_IMAGES_DAMMANN[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>

                {/* 一般社団法人ガールパワー様 */}
                <div className={`w-full mb-16 ${reveal('sponsor-girl-power')}`} data-reveal="sponsor-girl-power">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    一般社団法人ガールパワー様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_GIRL_POWER[0].src}
                        alt={SPONSOR_IMAGES_GIRL_POWER[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>

                {/* サラヤ株式会社様（Lactoferrin Lab.） */}
                <div className={`w-full mb-16 ${reveal('sponsor-saraya-full')}`} data-reveal="sponsor-saraya-full">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    サラヤ株式会社様（Lactoferrin Lab.）
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length + SPONSOR_IMAGES_GIRL_POWER.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_SARAYA_FULL[0].src}
                        alt={SPONSOR_IMAGES_SARAYA_FULL[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>

                {/* 株式会社リセルラボ様 */}
                <div className={`w-full ${reveal('sponsor-recelllab')}`} data-reveal="sponsor-recelllab">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    株式会社リセルラボ様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length + SPONSOR_IMAGES_GIRL_POWER.length + SPONSOR_IMAGES_SARAYA_FULL.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_RECELLLAB[0].src}
                        alt={SPONSOR_IMAGES_RECELLLAB[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>

                {/* 株式会社アドバンスト・メディカル・ケア様（est're） */}
                <div className={`w-full mb-16 ${reveal('sponsor-advanced-medical')}`} data-reveal="sponsor-advanced-medical">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    株式会社アドバンスト・メディカル・ケア様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length + SPONSOR_IMAGES_GIRL_POWER.length + SPONSOR_IMAGES_SARAYA_FULL.length + SPONSOR_IMAGES_RECELLLAB.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_ADVANCED_MEDICAL[0].src}
                        alt={SPONSOR_IMAGES_ADVANCED_MEDICAL[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>
              </div>

              {/* nanadecor by 有限会社Juliette様（2品・フル幅） */}
              <div className={`mb-16 ${reveal('sponsor-nanadecor')}`} data-reveal="sponsor-nanadecor">
                <h2
                  className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  nanadecor by 有限会社Juliette様
                </h2>
                <div
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 p-6 sm:p-8 rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: `${colors.cream}`,
                    boxShadow: '0 4px 24px rgba(74,69,67,0.08), 0 0 0 1px rgba(126,206,193,0.15)',
                  }}
                >
                  {SPONSOR_IMAGES_NANADECOR.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length + SPONSOR_IMAGES_GIRL_POWER.length + SPONSOR_IMAGES_SARAYA_FULL.length + SPONSOR_IMAGES_RECELLLAB.length + SPONSOR_IMAGES_ADVANCED_MEDICAL.length + i); setSponsorLightboxOpen(true); }}
                      className="block w-full text-left cursor-pointer group"
                    >
                      <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                        <img
                          src={img.src}
                          alt={img.alt}
                          className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <span className="sr-only">拡大表示</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 株式会社サンリオピューロランド様（2品・フル幅） */}
              <div className={`mb-16 ${reveal('sponsor-sanrio')}`} data-reveal="sponsor-sanrio">
                <h2
                  className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  株式会社サンリオピューロランド様
                </h2>
                <div
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 p-6 sm:p-8 rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: `${colors.cream}`,
                    boxShadow: '0 4px 24px rgba(74,69,67,0.08), 0 0 0 1px rgba(126,206,193,0.15)',
                  }}
                >
                  {SPONSOR_IMAGES_SANRIO_PUROLAND.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length + SPONSOR_IMAGES_GIRL_POWER.length + SPONSOR_IMAGES_SARAYA_FULL.length + SPONSOR_IMAGES_RECELLLAB.length + SPONSOR_IMAGES_ADVANCED_MEDICAL.length + SPONSOR_IMAGES_NANADECOR.length + i); setSponsorLightboxOpen(true); }}
                      className="block w-full text-left cursor-pointer group"
                    >
                      <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                        <img
                          src={img.src}
                          alt={img.alt}
                          className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <span className="sr-only">拡大表示</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* 築地・銀座WAGYU・日本生物製剤 を横並び */}
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 mb-16 ${reveal('sponsor-tsukiji')}`} data-reveal="sponsor-tsukiji">
                <div className="w-full">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    築地 共栄会 ボン・マルシェ様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length + SPONSOR_IMAGES_GIRL_POWER.length + SPONSOR_IMAGES_SARAYA_FULL.length + SPONSOR_IMAGES_RECELLLAB.length + SPONSOR_IMAGES_ADVANCED_MEDICAL.length + SPONSOR_IMAGES_NANADECOR.length + SPONSOR_IMAGES_SANRIO_PUROLAND.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_TSUKIJI_BON_MARCHE[0].src}
                        alt={SPONSOR_IMAGES_TSUKIJI_BON_MARCHE[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>

                <div className={`w-full ${reveal('sponsor-wagyu')}`} data-reveal="sponsor-wagyu">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    銀座WAGYU様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length + SPONSOR_IMAGES_GIRL_POWER.length + SPONSOR_IMAGES_SARAYA_FULL.length + SPONSOR_IMAGES_RECELLLAB.length + SPONSOR_IMAGES_ADVANCED_MEDICAL.length + SPONSOR_IMAGES_NANADECOR.length + SPONSOR_IMAGES_SANRIO_PUROLAND.length + SPONSOR_IMAGES_TSUKIJI_BON_MARCHE.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_GINZA_WAGYU[0].src}
                        alt={SPONSOR_IMAGES_GINZA_WAGYU[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>

                <div className={`w-full ${reveal('sponsor-jbp')}`} data-reveal="sponsor-jbp">
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-6"
                    style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                  >
                    株式会社日本生物製剤様
                  </h2>
                  <button
                    type="button"
                    onClick={() => { setSponsorLightboxIndex((SHOW_SARAYA ? SPONSOR_IMAGES_SARAYA.length : 0) + SPONSOR_IMAGES_ZUKA.length + SPONSOR_IMAGES_HERLIFELAB.length + (SHOW_ROHTO ? SPONSOR_IMAGES_ROHTO.length : 0) + SPONSOR_IMAGES_KIMONO_BEAUTY.length + SPONSOR_IMAGES_GINZA_HAGOROMO.length + SPONSOR_IMAGES_WIMMINS.length + SPONSOR_IMAGES_HOLLYWOOD.length + SPONSOR_IMAGES_MONEY_HEALTH.length + SPONSOR_IMAGES_DAMMANN.length + SPONSOR_IMAGES_GIRL_POWER.length + SPONSOR_IMAGES_SARAYA_FULL.length + SPONSOR_IMAGES_RECELLLAB.length + SPONSOR_IMAGES_ADVANCED_MEDICAL.length + SPONSOR_IMAGES_NANADECOR.length + SPONSOR_IMAGES_SANRIO_PUROLAND.length + SPONSOR_IMAGES_TSUKIJI_BON_MARCHE.length + SPONSOR_IMAGES_GINZA_WAGYU.length); setSponsorLightboxOpen(true); }}
                    className="block w-full text-left cursor-pointer group"
                  >
                    <div className="rounded-2xl overflow-hidden shadow-lg transition-transform group-hover:-translate-y-1 bg-gray-50">
                      <img
                        src={SPONSOR_IMAGES_JBP[0].src}
                        alt={SPONSOR_IMAGES_JBP[0].alt}
                        className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <span className="sr-only">拡大表示</span>
                  </button>
                </div>
              </div>

              {/* 協賛品に関する案内 */}
              <div className="mt-16 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500 leading-relaxed text-center max-w-2xl mx-auto">
                  本ページに掲載している協賛品は、協賛企業様よりご提供いただいた情報に基づくものです。
                  上記以外にも多数の協賛品をご用意しております。当日の抽選会にて、ぜひお楽しみください。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* インターミッション・アクト「着物ビューティーショー」 */}
        <section className="relative py-24 overflow-hidden" style={{ backgroundColor: colors.cream }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.mintLight} 0%, transparent 60%)`, filter: 'blur(100px)' }} />
            <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.coralLight} 0%, transparent 60%)`, filter: 'blur(100px)' }} />
          </div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div data-reveal="kimono-show" className={reveal('kimono-show')}>
              <div className="text-center mb-16">
                <span
                  className="inline-block px-5 py-2 text-xs font-bold tracking-[0.2em] uppercase mb-6"
                  style={{
                    backgroundColor: `${colors.mintLight}60`,
                    color: colors.mint,
                    borderRadius: '100px',
                  }}
                >
                  Kimono Beauty Show
                </span>
                <h2
                  className="text-3xl sm:text-4xl font-black tracking-tight"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  インターミッション・アクト「キモノビューティーショー」
                </h2>
                <p className="mt-4 text-gray-500">浅草神社奉納演舞 コロナ早期収束祈願の様子をご紹介</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-8">
                {[
                  { id: 'BrAVny43-lY', label: '浅草神社奉納演舞 コロナ早期収束祈願 1' },
                  { id: '9RWlKz2laeg', label: '浅草神社奉納演舞 コロナ早期収束祈願 2' },
                ].map((video, i) => (
                  <div key={i} className="group">
                    <div className="rounded-2xl overflow-hidden bg-gray-900 shadow-xl aspect-video">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={video.label}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                    <p className="mt-3 text-center font-bold" style={{ color: colors.warmGray }}>
                      {video.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 昨年度のイベントの様子 – ムービーギャラリー */}
        <section className="relative py-24 overflow-hidden" style={{ backgroundColor: colors.cream }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.mintLight} 0%, transparent 60%)`, filter: 'blur(100px)' }} />
            <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full opacity-20" style={{ background: `radial-gradient(circle, ${colors.coralLight} 0%, transparent 60%)`, filter: 'blur(100px)' }} />
          </div>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div data-reveal="gallery" className={reveal('gallery')}>
              <div className="text-center mb-16">
                <span
                  className="inline-block px-5 py-2 text-xs font-bold tracking-[0.2em] uppercase mb-6"
                  style={{
                    backgroundColor: `${colors.mintLight}60`,
                    color: colors.mint,
                    borderRadius: '100px',
                  }}
                >
                  Gallery
                </span>
                <h2
                  className="text-3xl sm:text-4xl font-black tracking-tight"
                  style={{ color: colors.warmGray, fontFamily: "'Shippori Mincho', serif" }}
                >
                  昨年度のイベントの様子
                </h2>
                <p className="mt-4 text-gray-500">昨年度、2025年3月8日に開催された、日本女性財団国際女性デーチャリティイベント2025の動画です。</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-8">
                {[
                  { id: 'wzAw47p7Vj8', label: '第１部オープニングアクト' },
                  { id: 'zSvruBjUsZU', label: '第１部ダイジェスト' },
                  { id: 'pFo2hVPVnrw', label: '第２部オープニングアクト' },
                  { id: 'EExrsyLZGFw', label: '第２部ダイジェスト' },
                ].map((video, i) => (
                  <div key={i} className="group">
                    <div className="rounded-2xl overflow-hidden bg-gray-900 shadow-xl aspect-video">
                      <iframe
                        src={`https://www.youtube.com/embed/${video.id}`}
                        title={video.label}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                    <p className="mt-3 text-center font-bold" style={{ color: colors.warmGray }}>
                      {video.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ダメ押し CTA */}
        <section className="relative py-24 overflow-hidden" style={{ backgroundColor: colors.coral }}>
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 left-20 w-96 h-96 rounded-full opacity-25 bg-white" style={{ filter: 'blur(100px)', animation: 'orbFloat 12s ease-in-out infinite' }} />
            <div className="absolute bottom-20 right-20 w-[500px] h-[500px] rounded-full opacity-20 bg-white" style={{ filter: 'blur(120px)', animation: 'orbFloatSlow 15s ease-in-out infinite' }} />
          </div>

          <div className={`max-w-4xl mx-auto px-4 text-center relative ${reveal('cta-final')}`} data-reveal="cta-final">
            <p className="text-white/90 text-lg sm:text-xl mb-6 leading-relaxed">
              迷っているなら、それが参加のサインかもしれません。
              <br />
              新しい出会いと、少しだけ前向きになった気持ちが、会場であなたを待っています。
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-10 leading-tight" style={{ fontFamily: "'Shippori Mincho', serif" }}>
              3月8日、ぜひ会場でお会いしましょう
            </h2>
            <a
              href="https://peatix.com/event/4805808/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-4 px-12 py-5 text-xl font-bold text-white rounded-xl bg-white/25 hover:bg-white/35 backdrop-blur-md transition-all duration-300 border-2 border-white hover:scale-105 shadow-lg"
            >
              <i className="ri-ticket-line text-3xl"></i>
              お申し込みはこちら
            </a>
            <p className="mt-8 text-white/80 text-sm">※ Peatixの外部サイトに移動します</p>
          </div>
        </section>
      </main>

      {/* 協賛品 Lightbox */}
      {sponsorLightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
          onClick={() => setSponsorLightboxOpen(false)}
        >
          <div className="absolute top-6 right-6 flex items-center gap-2 z-20">
            <a
              href={SPONSOR_IMAGES[sponsorLightboxIndex].src}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
              title="新しいタブで開く（大きく表示）"
            >
              <i className="ri-fullscreen-line text-2xl"></i>
            </a>
            <button
              onClick={() => setSponsorLightboxOpen(false)}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          {SPONSOR_IMAGES.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setSponsorLightboxIndex((i) => (i === 0 ? SPONSOR_IMAGES.length - 1 : i - 1)); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors z-20"
              >
                <i className="ri-arrow-left-s-line text-3xl"></i>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setSponsorLightboxIndex((i) => (i === SPONSOR_IMAGES.length - 1 ? 0 : i + 1)); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors z-20"
              >
                <i className="ri-arrow-right-s-line text-3xl"></i>
              </button>
            </>
          )}
          <div
            className="relative w-[95vw] max-w-7xl max-h-[95vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={SPONSOR_IMAGES[sponsorLightboxIndex].src}
              alt={SPONSOR_IMAGES[sponsorLightboxIndex].alt}
              className="max-w-full max-h-[90vh] w-full object-contain rounded-lg shadow-2xl"
            />
            <p className="mt-4 text-white text-sm">
              {sponsorLightboxIndex + 1} / {SPONSOR_IMAGES.length}
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
